import { TreeSelect, TreeSelectProps } from 'antd'
import React, { useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useTreeSelectShiftClick } from '../../../shared/hooks/useTreeSelectShiftClick'
import { Company } from '../../../types/company/Company'
import { useCompanyDimensionTreeWithRoot } from '../queries/useCompanyDimensionTreeWithRoot'

interface DimensionTreeSelectWithRootProps extends TreeSelectProps {
  companyId?: Company['id']
  virtualThreshold?: number
}

const DimensionTreeSelectWithRoot = ({
  companyId,
  virtualThreshold = 1000,
  onChange,
  treeCheckStrictly,
  treeCheckable,
  labelInValue,
  ...restProps
}: DimensionTreeSelectWithRootProps) => {
  const { t } = useTranslation()
  const { data: dimensionTreeWithRoot, isLoading } = useCompanyDimensionTreeWithRoot(companyId)

  // Count total nodes recursively
  const totalNodeCount = useMemo(() => {
    if (!dimensionTreeWithRoot) return 0

    const countNodes = (node: any): number => {
      return 1 + (node.children ? node.children.reduce((sum: number, child: any) => sum + countNodes(child), 0) : 0)
    }

    return countNodes(dimensionTreeWithRoot)
  }, [dimensionTreeWithRoot])

  // Memoize filter function for better performance
  const filterTreeNode = useCallback((inputVal: string, node: any) => {
    return node && node.name?.toLowerCase().includes(inputVal?.toLowerCase())
  }, [])

  // Enable virtual scrolling when node count exceeds threshold
  const shouldUseVirtual = totalNodeCount > virtualThreshold

  const treeDataArray = dimensionTreeWithRoot ? [dimensionTreeWithRoot] : []

  const { onChange: shiftClickOnChange, onDropdownVisibleChange } = useTreeSelectShiftClick({
    treeData: treeDataArray,
    fieldNames: { value: 'dimensionId', label: 'name', children: 'children' },
    onChange,
    treeCheckStrictly,
    labelInValue
  })

  return (
    <TreeSelect
      allowClear
      showSearch
      loading={isLoading}
      virtual={shouldUseVirtual}
      listHeight={shouldUseVirtual ? 256 : undefined}
      styles={{
        popup: {
          root: {
            minWidth: 350,
            maxHeight: shouldUseVirtual ? 300 : 500,
            overflow: 'auto'
          }
        }
      }}
      fieldNames={{
        label: 'name',
        value: 'dimensionId',
        children: 'children'
      }}
      filterTreeNode={filterTreeNode}
      treeNodeFilterProp="name"
      showCheckedStrategy={TreeSelect.SHOW_PARENT}
      treeData={treeDataArray}
      placeholder={t('contextDrawer:not-applied')}
      {...restProps}
      treeCheckStrictly={treeCheckStrictly}
      treeCheckable={treeCheckable}
      labelInValue={labelInValue}
      onChange={treeCheckable ? shiftClickOnChange : onChange}
      onOpenChange={onDropdownVisibleChange}
    />
  )
}

export default DimensionTreeSelectWithRoot
